import { defineStore } from "pinia";
import axios from "axios";
import { useAppStore } from "./useAppStore.js";
import { getToken, placeOrder } from "rdep-ecom-sdk";

const CART_STORAGE_KEY = "ecom.cart.items";
const CART_META_STORAGE_KEY = "ecom.cart.meta";
const CART_ID_STORAGE_KEY = "cart_id";
const CURRENT_USER_STORAGE_KEY = "auth.currentUser";
const PLACE_ORDER_RESPONSE_STORAGE_KEY = "place.order.api.response";

function getDefaultCartMeta() {
  return {
    cartId: null,
    customerMobileNumber: "",
    totalCoinValue: 0,
    netTotal: null,
    shippingCharge: null,
    orderTotal: null,
    loyaltyCards: [],
    guestSyncPending: false,
  };
}

function normalizeLoyaltyCards(cards) {
  const seenIds = new Set();

  return (Array.isArray(cards) ? cards : [])
    .map((card) => ({
      id: String(card?.id || "").trim(),
      type: String(card?.type || "Coupon").trim() || "Coupon",
    }))
    .filter((card) => {
      if (!card.id || seenIds.has(card.id)) return false;
      seenIds.add(card.id);
      return true;
    });
}

function mergeLoyaltyCards(existingCards, newCards) {
  return normalizeLoyaltyCards([
    ...(Array.isArray(existingCards) ? existingCards : []),
    ...(Array.isArray(newCards) ? newCards : []),
  ]);
}

async function postCartRequest(endpoint, payload) {
  const token = String(getToken() || "").trim();
  const response = await axios.post(endpoint, payload, {
    headers: token
      ? {
          Authorization: token.startsWith("Bearer ")
            ? token
            : `Bearer ${token}`,
        }
      : undefined,
  });

  return response?.data || response;
}

function getSavedAuthState() {
  try {
    const raw = window.localStorage.getItem(CURRENT_USER_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (error) {
    return null;
  }
}

function isUserAuthenticated() {
  const tokenFromSdk = String(getToken() || "").trim();
  if (tokenFromSdk) {
    return true;
  }

  const savedAuth = getSavedAuthState();
  const savedToken = String(savedAuth?.token || "").trim();
  return Boolean(savedToken);
}

function loadSavedItems() {
  try {
    const raw = window.localStorage.getItem(CART_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    console.warn("Failed to read cart cache", error);
    return [];
  }
}

function saveItems(items) {
  window.localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(items || []));
}

function loadSavedMeta() {
  try {
    const raw = window.localStorage.getItem(CART_META_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch (error) {
    console.warn("Failed to read cart meta cache", error);
    return {};
  }
}

function saveMeta(meta) {
  window.localStorage.setItem(
    CART_META_STORAGE_KEY,
    JSON.stringify(meta || {}),
  );
}

function savePlaceOrderResponse(response) {
  try {
    window.localStorage.setItem(
      PLACE_ORDER_RESPONSE_STORAGE_KEY,
      JSON.stringify(response || {}),
    );
  } catch (error) {
    console.warn("Failed to save place order response", error);
  }
}

function normalizeCountFromResponse(response) {
  const data = response?.data || response || {};
  const nestedData = data?.data || {};
  const directCount =
    data.totalItemCount ??
    data.total_item_count ??
    data.itemCount ??
    nestedData.totalItemCount ??
    nestedData.total_item_count ??
    nestedData.itemCount ??
    null;

  if (directCount !== null && directCount !== undefined && directCount !== "") {
    return Number(directCount) || 0;
  }

  const list =
    data.cartItems ||
    data.items ||
    data.item_list ||
    data.cart?.item_list ||
    nestedData.cartItems ||
    nestedData.items ||
    nestedData.item_list ||
    nestedData.cart?.item_list ||
    null;
  if (Array.isArray(list)) {
    return list.reduce((sum, item) => {
      const qty = Number(
        item?.quantity ?? item?.qty ?? item?.itemQty ?? item?.count ?? 1,
      );
      return sum + (Number.isFinite(qty) ? qty : 1);
    }, 0);
  }

  const statusMessage = String(
    data.statusMessage || nestedData.statusMessage || data.message || "",
  )
    .trim()
    .toLowerCase();
  const status = String(data.status || nestedData.status || "")
    .trim()
    .toLowerCase();

  if (status === "success" && statusMessage.includes("cart is empty")) {
    return 0;
  }

  return null;
}

function getResponseData(response) {
  return response?.data || response || {};
}

function getCartIdFromResponse(response) {
  const data = getResponseData(response);
  const nestedData = data?.data || {};
  return (
    data.cartId ||
    data.cart_id ||
    data.id ||
    data.cart?.cartId ||
    data.cart?.cart_id ||
    data.cart?.id ||
    nestedData.cartId ||
    nestedData.cart_id ||
    nestedData.id ||
    nestedData.cart?.cartId ||
    nestedData.cart?.cart_id ||
    nestedData.cart?.id ||
    ""
  );
}

function toNumericCartId(value) {
  const normalized = String(value ?? "").trim();
  if (!normalized) {
    return null;
  }

  const parsed = Number(normalized);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

function getMobileFromResponse(response) {
  const data = getResponseData(response);
  const nestedData = data?.data || {};
  return (
    data.customerMobileNumber ||
    data.customerMobile ||
    data.mobileNumber ||
    data.mobile ||
    data.cart?.customerMobileNumber ||
    data.cart?.customerMobile ||
    nestedData.customerMobileNumber ||
    nestedData.customerMobile ||
    nestedData.mobileNumber ||
    nestedData.mobile ||
    nestedData.cart?.customerMobileNumber ||
    nestedData.cart?.customerMobile ||
    ""
  );
}

function getTotalCoinValueFromResponse(response) {
  const data = getResponseData(response);
  const nestedData = data?.data || {};
  const raw =
    data.totalCoinValue ??
    data.total_coin_value ??
    nestedData.totalCoinValue ??
    nestedData.total_coin_value ??
    null;

  if (raw === null || raw === undefined || raw === "") {
    return null;
  }

  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : null;
}

function getNetTotalFromResponse(response) {
  const data = getResponseData(response);
  const nestedData = data?.data || {};
  const raw =
    data?.netTotal ??
    data?.net_total ??
    data?.cart?.netTotal ??
    data?.cart?.net_total ??
    nestedData?.netTotal ??
    nestedData?.net_total ??
    nestedData?.cart?.netTotal ??
    nestedData?.cart?.net_total;

  if (raw === null || raw === undefined || raw === "") {
    return null;
  }

  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : null;
}

function getShippingChargeFromResponse(response) {
  const data = getResponseData(response);
  const nestedData = data?.data || {};
  const raw =
    data.shippingCharge ??
    data.shipping_charge ??
    data.totalShippingCharge ??
    data.total_shipping_charge ??
    data.cart?.shippingCharge ??
    data.cart?.shipping_charge ??
    nestedData.shippingCharge ??
    nestedData.shipping_charge ??
    nestedData.totalShippingCharge ??
    nestedData.total_shipping_charge ??
    nestedData.cart?.shippingCharge ??
    nestedData.cart?.shipping_charge ??
    null;

  if (raw === null || raw === undefined || raw === "") {
    return null;
  }

  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : null;
}

function getOrderTotalFromResponse(response) {
  const data = getResponseData(response);
  const nestedData = data?.data || {};
  const raw =
    data.orderTotal ??
    data.order_total ??
    data.totalAmount ??
    data.total_amount ??
    data.grandTotal ??
    data.grand_total ??
    data.grossTotal ??
    data.gross_total ??
    data.cart?.orderTotal ??
    data.cart?.order_total ??
    data.cart?.totalAmount ??
    data.cart?.total_amount ??
    data.cart?.grandTotal ??
    data.cart?.grand_total ??
    data.cart?.grossTotal ??
    data.cart?.gross_total ??
    nestedData.orderTotal ??
    nestedData.order_total ??
    nestedData.totalAmount ??
    nestedData.total_amount ??
    nestedData.grandTotal ??
    nestedData.grand_total ??
    nestedData.grossTotal ??
    nestedData.gross_total ??
    nestedData.cart?.orderTotal ??
    nestedData.cart?.order_total ??
    nestedData.cart?.totalAmount ??
    nestedData.cart?.total_amount ??
    nestedData.cart?.grandTotal ??
    nestedData.cart?.grand_total ??
    nestedData.cart?.grossTotal ??
    nestedData.cart?.gross_total ??
    null;

  if (raw === null || raw === undefined || raw === "") {
    return null;
  }

  const parsed = Number(raw);
  return Number.isFinite(parsed) ? parsed : null;
}

function findArrayFieldWithPresence(source, candidateKeys = []) {
  if (!source || typeof source !== "object") {
    return { found: false, value: [] };
  }

  for (const key of candidateKeys) {
    if (!Object.prototype.hasOwnProperty.call(source, key)) {
      continue;
    }

    const candidateValue = source[key];
    if (Array.isArray(candidateValue)) {
      return { found: true, value: candidateValue };
    }
  }

  return { found: false, value: [] };
}

function getDiscountsArrayFromResponse(response) {
  const data = getResponseData(response);
  const nestedData = data?.data || {};

  const sources = [data, data?.cart, nestedData, nestedData?.cart].filter(
    Boolean,
  );

  for (const source of sources) {
    const matched = findArrayFieldWithPresence(source, [
      "discounts",
      "discount",
      "discountList",
      "discount_list",
      "discountDetails",
      "discount_details",
    ]);
    if (matched.found) {
      return matched;
    }
  }

  return { found: false, value: [] };
}

function getTaxesArrayFromResponse(response) {
  const data = getResponseData(response);
  const nestedData = data?.data || {};

  const sources = [data, data?.cart, nestedData, nestedData?.cart].filter(
    Boolean,
  );

  for (const source of sources) {
    const matched = findArrayFieldWithPresence(source, [
      "taxes",
      "tax",
      "taxList",
      "tax_list",
      "taxDetails",
      "tax_details",
      "taxBreakup",
      "tax_breakup",
    ]);
    if (matched.found) {
      return matched;
    }
  }

  return { found: false, value: [] };
}

function getOrderIdFromResponse(response) {
  const data = getResponseData(response);
  const nestedData = data?.data || {};
  return (
    data.orderId ||
    data.order_id ||
    data.orderNo ||
    data.orderNumber ||
    data.id ||
    nestedData.orderId ||
    nestedData.order_id ||
    nestedData.orderNo ||
    nestedData.orderNumber ||
    nestedData.id ||
    null
  );
}

function getImageUrlFromSource(source) {
  if (!source) return "";

  if (typeof source === "string") {
    return source;
  }

  if (Array.isArray(source)) {
    const firstValid = source
      .map((entry) => getImageUrlFromSource(entry))
      .find((url) => Boolean(url));
    return firstValid || "";
  }

  return (
    source.path ||
    source.url ||
    source.fileUrl ||
    source.imageUrl ||
    source.image ||
    source.location ||
    source.thumbnail ||
    ""
  );
}

function resolveCartItemImage(item, product) {
  return (
    getImageUrlFromSource(item?.image) ||
    getImageUrlFromSource(item?.imageUrl) ||
    getImageUrlFromSource(item?.filePath) ||
    getImageUrlFromSource(item?.thumbnail) ||
    getImageUrlFromSource(item?.files) ||
    getImageUrlFromSource(item?.productFiles) ||
    getImageUrlFromSource(item?.images) ||
    getImageUrlFromSource(product?.image) ||
    getImageUrlFromSource(product?.imageUrl) ||
    getImageUrlFromSource(product?.filePath) ||
    getImageUrlFromSource(product?.thumbnail) ||
    getImageUrlFromSource(product?.files) ||
    getImageUrlFromSource(product?.productFiles) ||
    getImageUrlFromSource(product?.images) ||
    getImageUrlFromSource(product?.product?.files) ||
    getImageUrlFromSource(product?.product?.productFiles) ||
    getImageUrlFromSource(product?.product?.images) ||
    ""
  );
}

function resolveStoreId(item, product) {
  const raw =
    item?.storeId ??
    item?.store_id ??
    item?.storeID ??
    item?.store?.id ??
    item?.store?.storeId ??
    item?.store?.store_id ??
    product?.storeId ??
    product?.store_id ??
    product?.storeID ??
    product?.store?.id ??
    product?.store?.storeId ??
    product?.store?.store_id;
  const parsed = Number(raw);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
}

function resolveStoreCode(item, product) {
  const raw =
    item?.storeCode ||
    item?.store_code ||
    item?.storeCd ||
    item?.store?.storeCode ||
    item?.store?.store_code ||
    item?.store?.code ||
    product?.storeCode ||
    product?.store_code ||
    product?.storeCd ||
    product?.store?.storeCode ||
    product?.store?.store_code ||
    product?.store?.code ||
    "";
  return String(raw).trim();
}

function extractStoreContext(source) {
  if (!source || typeof source !== "object") {
    return { storeId: undefined, storeCode: "" };
  }

  const parsedStoreId = Number(
    source.storeId ||
      source.store_id ||
      source.storeID ||
      source.store?.id ||
      source.store?.storeId ||
      source.tenantStoreId ||
      0,
  );
  const storeCode = String(
    source.storeCode ||
      source.store_code ||
      source.storeCd ||
      source.store?.storeCode ||
      source.store?.code ||
      "",
  ).trim();

  return {
    storeId:
      Number.isFinite(parsedStoreId) && parsedStoreId > 0
        ? parsedStoreId
        : undefined,
    storeCode,
  };
}

function getPayloadData(responsePayload) {
  if (!responsePayload || typeof responsePayload !== "object") {
    return null;
  }

  if (responsePayload?.data?.data) return responsePayload.data.data;
  if (responsePayload?.data) return responsePayload.data;
  return responsePayload;
}

function normalizeItemFromResponse(item) {
  if (!item || typeof item !== "object") {
    return null;
  }

  const product = item.product || item.productDetails || item;
  const productId = String(
    item.productId || product.productId || product.id || "",
  );
  const sku = String(item.sku || product.sku || "");
  const key = productId || sku;

  if (!key) {
    return null;
  }

  const resolvedQuantity = Number(
    item.quantity ??
      item.qty ??
      item.itemQty ??
      item.item_qty ??
      item.itemQuantity ??
      item.orderQty ??
      item.order_qty ??
      item.count ??
      product.quantity ??
      product.qty ??
      product.itemQty ??
      product.itemQuantity ??
      1,
  );

  return {
    key,
    productId,
    sku,
    storeId: resolveStoreId(item, product),
    storeCode: resolveStoreCode(item, product),
    name: item.name || product.name || "Product",
    netPrice: item.netPrice ?? null,
    price:
      item.netPrice ??
      item.net_price ??
      item.actualPrice ??
      item.actual_price ??
      product.netPrice ??
      product.net_price ??
      product.actualPrice ??
      product.actual_price ??
      item.price ??
      item.listPrice ??
      product.listPrice ??
      item.mrp ??
      product.mrp ??
      null,
    coinValue:
      item.coinValue ??
      item.coin_value ??
      item.totalCoinValue ??
      product.coinValue ??
      product.coin_value ??
      null,
    shippingCharge:
      item.shippingCharge ??
      item.shipping_charge ??
      product.shippingCharge ??
      product.shipping_charge ??
      0,
    hsnCode:
      item.hsnCode ??
      item.hsn_code ??
      product.hsnCode ??
      product.hsn_code ??
      null,
    hsnSlabCutoff:
      item.hsnSlabCutoff ??
      item.hsn_slab_cutoff ??
      product.hsnSlabCutoff ??
      product.hsn_slab_cutoff ??
      null,
    hsnSlabRate:
      item.hsnSlabRate ??
      item.hsn_slab_rate ??
      product.hsnSlabRate ??
      product.hsn_slab_rate ??
      null,
    scflag: item.scflag ?? product.scflag ?? null,
    gstctype: item.gstctype ?? product.gstctype ?? null,
    taxes: Array.isArray(item?.taxes) ? item.taxes : undefined,
    discounts: Array.isArray(item?.discounts) ? item.discounts : undefined,
    image: resolveCartItemImage(item, product),
    quantity:
      Number.isFinite(resolvedQuantity) && resolvedQuantity > 0
        ? resolvedQuantity
        : 1,
  };
}

function looksLikeCartItem(candidate) {
  if (!candidate || typeof candidate !== "object" || Array.isArray(candidate)) {
    return false;
  }

  return Boolean(
    candidate.sku ||
      candidate.productId ||
      candidate.itemId ||
      candidate.product ||
      candidate.productDetails,
  );
}

function findCartItemsInObject(source, depth = 0, visited = new Set()) {
  if (!source || typeof source !== "object" || depth > 6) {
    return null;
  }

  if (visited.has(source)) {
    return null;
  }
  visited.add(source);

  if (Array.isArray(source)) {
    if (source.length && source.some((entry) => looksLikeCartItem(entry))) {
      return source;
    }

    for (const entry of source) {
      const nested = findCartItemsInObject(entry, depth + 1, visited);
      if (Array.isArray(nested) && nested.length) {
        return nested;
      }
    }
    return null;
  }

  for (const value of Object.values(source)) {
    const nested = findCartItemsInObject(value, depth + 1, visited);
    if (Array.isArray(nested) && nested.length) {
      return nested;
    }
  }

  return null;
}

function normalizeItemsFromResponse(response) {
  const data = getResponseData(response);
  const nestedData = data?.data || {};
  const possibleLists = [
    data.itemResult,
    data.item_result,
    data.cartItems,
    data.cartItemList,
    data.cart_item_list,
    data.cartItemResponses,
    data.cart_item_responses,
    data.items,
    data.item_list,
    data.cart?.item_list,
    data.cart?.itemResult,
    data.cart?.item_result,
    data.cart?.cartItems,
    data.cart?.cartItemList,
    data.cart?.cart_item_list,
    data.cart?.cartItemResponses,
    data.cart?.items,
    nestedData.itemResult,
    nestedData.item_result,
    nestedData.cartItems,
    nestedData.cartItemList,
    nestedData.cart_item_list,
    nestedData.cartItemResponses,
    nestedData.cart_item_responses,
    nestedData.items,
    nestedData.item_list,
    nestedData.cart?.item_list,
    nestedData.cart?.itemResult,
    nestedData.cart?.item_result,
    nestedData.cart?.cartItems,
    nestedData.cart?.cartItemList,
    nestedData.cart?.cart_item_list,
    nestedData.cart?.cartItemResponses,
    nestedData.cart?.items,
  ];

  const source =
    possibleLists.find((list) => Array.isArray(list) && list.length) ||
    findCartItemsInObject(data) ||
    findCartItemsInObject(nestedData);

  if (!source) {
    return null;
  }

  return source
    .map((item) => normalizeItemFromResponse(item))
    .filter((item) => Boolean(item));
}

function getRefreshItemResultList(response) {
  const data = getResponseData(response);
  const nestedData = data?.data || {};

  const candidates = [
    data?.itemResult,
    data?.item_result,
    data?.cart?.itemResult,
    data?.cart?.item_result,
    nestedData?.itemResult,
    nestedData?.item_result,
    nestedData?.cart?.itemResult,
    nestedData?.cart?.item_result,
  ];

  const firstArray = candidates.find((entry) => Array.isArray(entry));
  return Array.isArray(firstArray) ? firstArray : [];
}

function getItemLookupKeys(item) {
  return [item?.sku, item?.productId, item?.id, item?.productCode, item?.key]
    .map((value) => String(value || "").trim())
    .filter(Boolean);
}

export const useCartStore = defineStore("cart", {
  state: () => ({
    items: loadSavedItems(),
    totalCount: 0,
    lastResponse: null,
    cartMeta: {
      ...getDefaultCartMeta(),
      ...loadSavedMeta(),
    },
  }),

  getters: {
    hasItems: (state) => state.items.length > 0,
    grandTotalAmount: (state) =>
      state.items.reduce((sum, item) => {
        const price = Number(item.price) || 0;
        const quantity = Number(item.quantity) || 1;
        return sum + price * quantity;
      }, 0),
    grandTotalCoinValue: (state) => {
      const fromMeta = Number(state.cartMeta?.totalCoinValue);
      if (Number.isFinite(fromMeta) && fromMeta >= 0) {
        return fromMeta;
      }

      return state.items.reduce((sum, item) => {
        const coin = Number(item.coinValue) || 0;
        const quantity = Number(item.quantity) || 1;
        return sum + coin * quantity;
      }, 0);
    },
    grandTotalShippingCharge: (state) =>
      state.items.reduce((sum, item) => {
        const shipping = Number(item.shippingCharge) || 0;
        return sum + shipping;
      }, 0),
    orderNetTotal: (state) => {
      const fromMeta = Number(state.cartMeta?.netTotal);
      if (Number.isFinite(fromMeta) && (fromMeta > 0 || !state.items.length)) {
        return fromMeta;
      }

      return state.items.reduce((sum, item) => {
        const netPrice = Number(
          item?.netPrice ?? item?.net_price ?? item?.price ?? item?.mrp ?? 0,
        );
        const quantity = Number(item?.quantity || 1) || 1;
        return sum + (Number.isFinite(netPrice) ? netPrice * quantity : 0);
      }, 0);
    },
    orderShippingCharge: (state) => {
      const fromMeta = Number(state.cartMeta?.shippingCharge);
      if (Number.isFinite(fromMeta) && (fromMeta > 0 || !state.items.length)) {
        return fromMeta;
      }

      return state.items.reduce((sum, item) => {
        const shipping = Number(
          item?.shippingCharge ?? item?.shipping_charge ?? 0,
        );
        return sum + (Number.isFinite(shipping) ? shipping : 0);
      }, 0);
    },
    orderTotalAmountFromResponse: (state) => {
      const fromMeta = Number(state.cartMeta?.orderTotal);
      if (Number.isFinite(fromMeta)) {
        return fromMeta;
      }
      return 0;
    },
    displayOrderTotal() {
      const subtotal = Number(this.orderNetTotal) || 0;
      const shipping = Number(this.orderShippingCharge) || 0;
      return subtotal + shipping;
    },
  },

  actions: {
    async resolveStoreContextForCart() {
      const appStore = useAppStore();

      const fromTenantDetails = extractStoreContext(appStore.tenantDetails);
      if (fromTenantDetails.storeId && fromTenantDetails.storeCode) {
        return fromTenantDetails;
      }

      const domainName = String(
        appStore.domainName || window.location.host || "",
      ).trim();
      const baseURL = String(
        appStore.tenantApiBaseUrl || appStore.baseUrl || "",
      ).trim();
      if (!domainName || !baseURL) {
        return fromTenantDetails;
      }

      try {
        const encodedDomain = encodeURIComponent(domainName);
        const response = await fetch(
          `${baseURL}/auth-service/noauth/store/info/${encodedDomain}`,
          {
            method: "GET",
          },
        );

        if (!response.ok) {
          return fromTenantDetails;
        }

        const payload = await response.json();
        const data = getPayloadData(payload);
        if (data) {
          appStore.setTenantDetails(data);
        }

        const fromApi = extractStoreContext(data);
        if (fromApi.storeId && fromApi.storeCode) {
          return fromApi;
        }
      } catch (error) {
        // Keep removal flow resilient; validation later handles missing fields.
      }

      return fromTenantDetails;
    },

    initializeCount() {
      this.totalCount = this.items.reduce(
        (sum, item) => sum + (Number(item.quantity) || 1),
        0,
      );
    },

    persistCartState() {
      saveItems(this.items);
      saveMeta(this.cartMeta);
      this.initializeCount();
    },

    syncMetaFromResponse(response) {
      const cartId = toNumericCartId(getCartIdFromResponse(response));
      const mobile = String(getMobileFromResponse(response) || "");
      const totalCoinValue = getTotalCoinValueFromResponse(response);
      const netTotal = getNetTotalFromResponse(response);
      const shippingCharge = getShippingChargeFromResponse(response);
      const orderTotal = getOrderTotalFromResponse(response);

      this.cartMeta = {
        ...this.cartMeta,
        cartId: cartId || toNumericCartId(this.cartMeta.cartId) || null,
        customerMobileNumber:
          mobile || this.cartMeta.customerMobileNumber || "",
        totalCoinValue:
          totalCoinValue !== null
            ? totalCoinValue
            : Number(this.cartMeta.totalCoinValue) || 0,
        netTotal:
          netTotal !== null ? netTotal : Number(this.cartMeta.netTotal) || null,
        shippingCharge:
          shippingCharge !== null
            ? shippingCharge
            : Number(this.cartMeta.shippingCharge) || null,
        orderTotal:
          orderTotal !== null
            ? orderTotal
            : Number(this.cartMeta.orderTotal) || null,
      };
    },

    syncItemsFromResponse(response) {
      const normalizedItems = normalizeItemsFromResponse(response);
      const serverCount = normalizeCountFromResponse(response);

      if (Array.isArray(normalizedItems)) {
        this.items = normalizedItems;
      } else if (isUserAuthenticated()) {
        // Clear only when API explicitly says cart is empty.
        if (serverCount === 0) {
          this.items = [];
        }
      }

      if (serverCount !== null) {
        this.totalCount = serverCount;
      } else if (!Array.isArray(normalizedItems) && isUserAuthenticated()) {
        this.totalCount = this.items.reduce(
          (sum, item) => sum + (Number(item?.quantity) || 1),
          0,
        );
      } else {
        this.totalCount = this.items.reduce(
          (sum, item) => sum + (Number(item?.quantity) || 1),
          0,
        );
      }

      this.syncMetaFromResponse(response);
      this.persistCartState();
    },

    applyRefreshResponse(response) {
      const normalizedItems = normalizeItemsFromResponse(response);
      const serverCount = normalizeCountFromResponse(response);

      if (Array.isArray(normalizedItems)) {
        this.items = normalizedItems;
      } else if (serverCount === 0) {
        // Some refresh responses return only count without item list.
        this.items = [];
      } else if (Number.isFinite(serverCount) && serverCount > 0) {
        const localCount = this.items.reduce(
          (sum, item) => sum + (Number(item?.quantity) || 1),
          0,
        );

        if (localCount !== serverCount) {
          // Keep current items when payload shape is unknown, but sync count.
          this.totalCount = serverCount;
        }
      }

      if (serverCount !== null) {
        this.totalCount = serverCount;
      } else {
        this.totalCount = this.items.reduce(
          (sum, item) => sum + (Number(item?.quantity) || 1),
          0,
        );
      }

      this.syncMetaFromResponse(response);
      this.persistCartState();
    },

    async addItemWithCartContext(
      cartItems = [],
      loyaltyCards = this.cartMeta?.loyaltyCards,
    ) {
      const normalizedItems = Array.isArray(cartItems) ? cartItems : [];
      if (!normalizedItems.length) {
        return null;
      }

      const cartId = toNumericCartId(this.cartMeta?.cartId);
      try {
        return await postCartRequest("/cart-service/ws/cart/addItemtoCart", {
          operation: "AddItem",
          ...(cartId ? { cartId } : {}),
          cartItems: normalizedItems,
          loyaltyCards: normalizeLoyaltyCards(loyaltyCards),
        });
      } catch (error) {
        if (!cartId) {
          throw error;
        }

        // If server rejects the old cart after checkout, create a new one.
        return await postCartRequest("/cart-service/ws/cart/addItemtoCart", {
          operation: "AddItem",
          cartItems: normalizedItems,
          loyaltyCards: normalizeLoyaltyCards(loyaltyCards),
        });
      }
    },

    upsertLocalItem(product, quantity = 1) {
      const productId = String(product?.productId || product?.id || "");
      const sku = String(product?.sku || "");
      const key = productId || sku;

      if (!key) {
        return;
      }

      const existingIndex = this.items.findIndex((item) => item.key === key);
      if (existingIndex > -1) {
        const existing = this.items[existingIndex];
        this.items[existingIndex] = {
          ...existing,
          quantity: (Number(existing.quantity) || 1) + quantity,
        };
      } else {
        this.items.push({
          key,
          productId,
          sku,
          name: product?.name || "Product",
          netPrice: product?.netPrice ?? product?.net_price ?? null,
          price:
            product?.net_price ??
            product?.netPrice ??
            product?.actualPrice ??
            product?.listPrice ??
            product?.mrp ??
            null,
          coinValue:
            product?.coinValue ??
            product?.coin_value ??
            product?.totalCoinValue ??
            null,
          shippingCharge:
            product?.shippingCharge ?? product?.shipping_charge ?? 0,
          image: resolveCartItemImage(product, product),
          quantity,
        });
      }

      this.persistCartState();
    },

    getLineTotal(item) {
      const netPrice = Number(item?.netPrice);
      return Number.isFinite(netPrice) ? netPrice : 0;
    },

    updateItemQuantity(itemKey, quantity) {
      const key = String(itemKey || "");
      if (!key) {
        return;
      }

      const index = this.items.findIndex((item) => item.key === key);
      if (index === -1) {
        return;
      }

      const nextQty = Math.max(1, Number(quantity) || 1);
      this.items[index] = {
        ...this.items[index],
        quantity: nextQty,
      };

      this.persistCartState();
    },

    async increaseItemQuantity(itemKey) {
      const key = String(itemKey || "");
      const existing = this.items.find((item) => item.key === key);
      if (!existing) {
        return;
      }

      const nextQuantity = (Number(existing.quantity) || 1) + 1;
      await this.updateQuantityByApi(existing, nextQuantity);
    },

    async decreaseItemQuantity(itemKey) {
      const key = String(itemKey || "");
      const existing = this.items.find((item) => item.key === key);
      if (!existing) {
        return;
      }

      const nextQuantity = (Number(existing.quantity) || 1) - 1;
      if (nextQuantity <= 0) {
        await this.removeItem(itemKey);
        return;
      }

      await this.updateQuantityByApi(existing, nextQuantity);
    },

    async removeItem(itemKey) {
      const key = String(itemKey || "");
      let existing = this.items.find((item) => item.key === key);

      if (!existing) {
        return;
      }

      if (!isUserAuthenticated()) {
        this.cartMeta = {
          ...this.cartMeta,
          guestSyncPending: true,
        };
        this.items = this.items.filter((item) => item.key !== key);
        this.persistCartState();
        return;
      }

      if (this.cartMeta?.guestSyncPending) {
        await this.syncGuestCartAfterLogin();
        existing = this.items.find((item) => item.key === key);
        if (!existing) {
          await this.refreshCartData();
          return;
        }
      }

      let cartId = toNumericCartId(this.cartMeta?.cartId);
      if (!cartId) {
        await this.syncGuestCartAfterLogin();
        cartId = toNumericCartId(this.cartMeta?.cartId);
      }

      if (!cartId) {
        await this.refreshCartData();
        throw new Error("Cart not found for remove item API.");
      }

      try {
        let removeTarget = existing;
        if (!removeTarget.storeId || !removeTarget.storeCode) {
          await this.refreshCartData();
          const refreshed = this.items.find((item) => item.key === key);
          if (refreshed) {
            removeTarget = refreshed;
          }
        }

        if (!removeTarget.storeId || !removeTarget.storeCode) {
          const storeContext = await this.resolveStoreContextForCart();
          removeTarget = {
            ...removeTarget,
            storeId: removeTarget.storeId || storeContext.storeId,
            storeCode: removeTarget.storeCode || storeContext.storeCode,
          };
        }

        const parsedCartId = Number(cartId);
        const payloadCartId =
          Number.isFinite(parsedCartId) && parsedCartId > 0
            ? parsedCartId
            : cartId;
        const parsedStoreId = Number(removeTarget.storeId);
        const payloadStoreCode = String(removeTarget.storeCode || "").trim();
        const payloadSku = String(removeTarget.sku || "").trim();

        if (
          !(Number.isFinite(parsedStoreId) && parsedStoreId > 0) ||
          !payloadStoreCode ||
          !payloadSku
        ) {
          throw new Error("Store details missing for remove item payload.");
        }

        const response = await postCartRequest(
          "/cart-service/ws/cart/removeItem",
          {
            operation: "RemoveItem",
            cartId: payloadCartId,
            loyaltyCards: normalizeLoyaltyCards(this.cartMeta?.loyaltyCards),
            cartItems: [
              {
                storeId: parsedStoreId,
                sku: payloadSku,
                storeCode: payloadStoreCode,
              },
            ],
          },
        );

        this.lastResponse = response;
        this.syncItemsFromResponse(response);
        await this.refreshCartData();
      } catch (error) {
        await this.refreshCartData();
        throw error;
      }
    },

    async updateQuantityByApi(item, quantity, isRemove = false) {
      const nextQty = Number(quantity) || 0;
      const authenticated = isUserAuthenticated();
      const previousQty = Number(item?.quantity) || 1;

      if (!authenticated) {
        this.cartMeta = {
          ...this.cartMeta,
          guestSyncPending: true,
        };

        if (isRemove || nextQty <= 0) {
          this.items = this.items.filter((row) => row.key !== item.key);
        } else {
          this.updateItemQuantity(item.key, nextQty);
        }

        this.persistCartState();
        return;
      }

      if (this.cartMeta?.guestSyncPending) {
        await this.syncGuestCartAfterLogin();
      }

      let cartId = toNumericCartId(this.cartMeta?.cartId);

      if (!cartId) {
        await this.syncGuestCartAfterLogin();
        cartId = toNumericCartId(this.cartMeta?.cartId);

        if (!cartId) {
          this.cartMeta = {
            ...this.cartMeta,
            guestSyncPending: true,
          };
          if (isRemove || nextQty <= 0) {
            this.items = this.items.filter((row) => row.key !== item.key);
          } else {
            this.updateItemQuantity(item.key, nextQty);
          }
          this.persistCartState();
          return;
        }
      }

      try {
        // Optimistically update UI so quantity changes are visible instantly.
        this.updateItemQuantity(item.key, nextQty);

        let updateTarget = item;
        if (!updateTarget?.storeId || !updateTarget?.storeCode) {
          await this.refreshCartData();
          const refreshed = this.items.find((row) => row.key === item.key);
          if (refreshed) {
            updateTarget = refreshed;
          }
        }

        if (!updateTarget?.storeId || !updateTarget?.storeCode) {
          const storeContext = await this.resolveStoreContextForCart();
          updateTarget = {
            ...updateTarget,
            storeId: updateTarget?.storeId || storeContext.storeId,
            storeCode: updateTarget?.storeCode || storeContext.storeCode,
          };
        }

        const parsedStoreId = Number(updateTarget?.storeId);
        const payloadStoreCode = String(updateTarget?.storeCode || "").trim();
        const payloadSku = String(updateTarget?.sku || item?.sku || "").trim();

        if (
          !(Number.isFinite(parsedStoreId) && parsedStoreId > 0) ||
          !payloadStoreCode ||
          !payloadSku
        ) {
          throw new Error("Store details missing for update quantity payload.");
        }

        const response = await postCartRequest(
          "/cart-service/ws/cart/updateItemQty",
          {
            operation: "UpdateItemQuantity",
            cartId,
            loyaltyCards: normalizeLoyaltyCards(this.cartMeta?.loyaltyCards),
            cartItems: [
              {
                storeId: parsedStoreId,
                newQuantity: nextQty,
                storeCode: payloadStoreCode,
                sku: payloadSku,
              },
            ],
          },
        );

        this.lastResponse = response;

        const normalizedFromUpdate = normalizeItemsFromResponse(response);
        if (
          Array.isArray(normalizedFromUpdate) &&
          normalizedFromUpdate.length
        ) {
          this.syncItemsFromResponse(response);
        } else {
          this.syncMetaFromResponse(response);
          this.persistCartState();
        }

        // Final state must always come from refresh API for logged-in users.
        await this.refreshCartData();
      } catch (error) {
        this.cartMeta = {
          ...this.cartMeta,
          guestSyncPending: true,
        };
        if (isRemove || nextQty <= 0) {
          this.items = this.items.filter((row) => row.key !== item.key);
        } else {
          this.updateItemQuantity(item.key, previousQty);
        }
        throw error;
      }
    },

    async refreshCartData() {
      if (!isUserAuthenticated()) {
        this.persistCartState();
        return null;
      }

      const cartId = toNumericCartId(this.cartMeta?.cartId);
      if (!cartId) {
        this.persistCartState();
        return null;
      }

      try {
        const response = await postCartRequest(
          "/cart-service/ws/cart/refreshCart",
          {
            operation: "Refresh cart",
            cartId,
            customerMobileNumber:
              this.cartMeta?.customerMobileNumber || undefined,
            customerName: "",
            customerEmail: "",
            loyaltyCards: normalizeLoyaltyCards(this.cartMeta?.loyaltyCards),
          },
        );

        this.lastResponse = response;
        this.applyRefreshResponse(response);
        return response;
      } catch (error) {
        this.persistCartState();
        return null;
      }
    },

    async placeOrderFromCart({
      shippingAddress = null,
      billingAddress = null,
      customerGSTNumber = "",
    } = {}) {
      if (!isUserAuthenticated()) {
        throw new Error("Please login to continue checkout.");
      }

      if (this.cartMeta?.guestSyncPending) {
        await this.syncGuestCartAfterLogin();
      }

      if (
        !toNumericCartId(this.cartMeta?.cartId) &&
        Array.isArray(this.items) &&
        this.items.length
      ) {
        // Recover from stale state where items exist but cartId was never created.
        await this.syncGuestCartAfterLogin();
      }

      const refreshResponse = await this.refreshCartData();

      const cartId = toNumericCartId(this.cartMeta?.cartId);
      if (!cartId) {
        throw new Error("Cart not found. Please add products and try again.");
      }

      if (!Array.isArray(this.items) || !this.items.length) {
        throw new Error("Cart is empty. Please add products and try again.");
      }

      const storeContext = await this.resolveStoreContextForCart();
      const fallbackStoreId = Number(storeContext?.storeId) || undefined;
      const fallbackStoreCode = String(storeContext?.storeCode || "").trim();
      const refreshedItems = normalizeItemsFromResponse(refreshResponse) || [];
      const rawRefreshItems = getRefreshItemResultList(refreshResponse);
      const refreshedItemBySku = new Map(
        refreshedItems
          .map((entry) => [String(entry?.sku || "").trim(), entry])
          .filter(([sku]) => Boolean(sku)),
      );
      const rawRefreshItemByKey = new Map();
      rawRefreshItems.forEach((entry) => {
        getItemLookupKeys(entry).forEach((lookupKey) => {
          if (!rawRefreshItemByKey.has(lookupKey)) {
            rawRefreshItemByKey.set(lookupKey, entry);
          }
        });
      });

      const cartItems = this.items.map((item, index) => {
        const lookupKeys = getItemLookupKeys(item);
        const matchedRawRefreshItem =
          lookupKeys
            .map((lookupKey) => rawRefreshItemByKey.get(lookupKey))
            .find(Boolean) || null;
        const matchedRefreshItem = refreshedItemBySku.get(
          String(item?.sku || "").trim(),
        );
        const storeId = Number(item?.storeId) || fallbackStoreId;
        const storeCode = String(
          item?.storeCode || fallbackStoreCode || "",
        ).trim();
        const quantity = Number(item?.quantity || 1) || 1;
        const price = Number(item?.price || 0) || 0;
        const mrp =
          Number(item?.mrp ?? item?.netPrice ?? item?.price ?? 0) || 0;
        const coinValue =
          Number(
            item?.coinValue ??
              item?.coin_value ??
              item?.totalCoinValue ??
              item?.total_coin_value ??
              item?.product?.coinValue ??
              item?.product?.coin_value ??
              0,
          ) || 0;
        const shippingCharge =
          Number(
            item?.shippingCharge ??
              item?.shipping_charge ??
              item?.product?.shippingCharge ??
              item?.product?.shipping_charge ??
              0,
          ) || 0;
        const hsnCode =
          Number(
            item?.hsnCode ??
              item?.hsn_code ??
              matchedRawRefreshItem?.hsnCode ??
              matchedRawRefreshItem?.hsn_code ??
              matchedRefreshItem?.hsnCode ??
              matchedRefreshItem?.hsn_code ??
              0,
          ) || 0;
        const hsnSlabCutoff =
          Number(
            item?.hsnSlabCutoff ??
              item?.hsn_slab_cutoff ??
              matchedRawRefreshItem?.hsnSlabCutoff ??
              matchedRawRefreshItem?.hsn_slab_cutoff ??
              matchedRefreshItem?.hsnSlabCutoff ??
              matchedRefreshItem?.hsn_slab_cutoff ??
              0,
          ) || 0;
        const hsnSlabRate =
          Number(
            item?.hsnSlabRate ??
              item?.hsn_slab_rate ??
              matchedRawRefreshItem?.hsnSlabRate ??
              matchedRawRefreshItem?.hsn_slab_rate ??
              matchedRefreshItem?.hsnSlabRate ??
              matchedRefreshItem?.hsn_slab_rate ??
              0,
          ) || 0;
        const scflag =
          Number(
            item?.scflag ??
              matchedRawRefreshItem?.scflag ??
              matchedRefreshItem?.scflag ??
              0,
          ) || 0;
        const gstctype =
          Number(
            item?.gstctype ??
              matchedRawRefreshItem?.gstctype ??
              matchedRefreshItem?.gstctype ??
              0,
          ) || 0;
        const taxes = Array.isArray(matchedRawRefreshItem?.taxes)
          ? matchedRawRefreshItem.taxes
          : Array.isArray(matchedRefreshItem?.taxes)
          ? matchedRefreshItem.taxes
          : Array.isArray(item?.taxes)
          ? item.taxes
          : null;
        const discounts = Array.isArray(matchedRawRefreshItem?.discounts)
          ? matchedRawRefreshItem.discounts
          : Array.isArray(matchedRefreshItem?.discounts)
          ? matchedRefreshItem.discounts
          : Array.isArray(item?.discounts)
          ? item.discounts
          : null;

        return {
          id: String(item?.productId || item?.key || index + 1),
          sku: String(item?.sku || ""),
          name: item?.name || "Product",
          deptNmbr: String(item?.deptNmbr || item?.deptNumber || ""),
          baseAmnt: price,
          price,
          MRP: mrp,
          coin_value: coinValue,
          shipping_charge: shippingCharge,
          quantity,
          unit: String(item?.unit || "NOS"),
          scantype: String(item?.scantype || "MANUAL"),
          pluFlag: String(item?.pluFlag || "N"),
          hsnCode,
          hsnSlabCutoff,
          hsnSlabRate,
          scflag,
          gstctype,
          sequence: index + 1,
          vatbit: String(item?.vatbit || "N"),
          storeCode,
          ...(Array.isArray(taxes) ? { taxes } : {}),
          ...(Array.isArray(discounts) ? { discounts } : {}),
          ...(storeId ? { storeId } : {}),
        };
      });

      const now = new Date();
      const timeMs = String(now.getTime());
      const refreshedGrossTotal = getOrderTotalFromResponse(refreshResponse);
      const refreshedNetTotal = getNetTotalFromResponse(refreshResponse);
      const discountsFromRefresh =
        getDiscountsArrayFromResponse(refreshResponse);
      const taxesFromRefresh = getTaxesArrayFromResponse(refreshResponse);
      const grossTotal =
        refreshedGrossTotal !== null
          ? refreshedGrossTotal
          : Number(this.cartMeta?.orderTotal);
      const netTotal =
        refreshedNetTotal !== null
          ? refreshedNetTotal
          : Number(this.cartMeta?.netTotal);
      const shippingCharge = Number(this.grandTotalShippingCharge) || 0;

      const normalizedCustomerGSTNumber = String(
        customerGSTNumber || "",
      ).trim();

      const normalizeOrderAddress = (address) => {
        if (!address || typeof address !== "object") return null;

        const contactPerson = String(
          address.contactPerson || address.fullName || address.name || "",
        ).trim();

        return {
          ...address,
          contactPerson,
        };
      };

      const normalizedShippingAddress = normalizeOrderAddress(shippingAddress);
      const normalizedBillingAddress = normalizeOrderAddress(billingAddress);

      const requestPayload = {
        cartId,
        task: "PLACE_ORDER",
        docNumber: 1,
        discounts: [],
        grossTotal: Number.isFinite(grossTotal) ? grossTotal : 0,
        netTotal: Number.isFinite(netTotal) ? netTotal : 0,
        shipping_charge: shippingCharge,
        roundedValue: 0,
        sessionId: `${timeMs}${Math.floor(Math.random() * 1000000)
          .toString()
          .padStart(6, "0")}`,
        startTime: timeMs,
        ...(discountsFromRefresh.found
          ? { discounts: discountsFromRefresh.value }
          : {}),
        ...(taxesFromRefresh.found ? { taxes: taxesFromRefresh.value } : {}),
        cartItems,
        ...(fallbackStoreCode ? { store_code: fallbackStoreCode } : {}),
        ...(fallbackStoreId ? { organization_id: fallbackStoreId } : {}),
        timestamp: now.toISOString().slice(0, 19).replace("T", " "),
        loyaltyCards: normalizeLoyaltyCards(this.cartMeta?.loyaltyCards),
        ...(normalizedShippingAddress
          ? { shippingAddress: normalizedShippingAddress }
          : {}),
        ...(normalizedBillingAddress
          ? { billingAddress: normalizedBillingAddress }
          : {}),
        customerGSTNumber: normalizedCustomerGSTNumber || null,
      };

      const response = await placeOrder(requestPayload);
      this.lastResponse = response;
      savePlaceOrderResponse(response);

      return {
        response,
        orderId: getOrderIdFromResponse(response),
        requestPayload,
      };
    },

    async clearCartAfterPayment() {
      // const authenticated = isUserAuthenticated();
      // const itemKeys = Array.isArray(this.items)
      //   ? this.items.map((item) => item?.key).filter((key) => Boolean(key))
      //   : [];

      // if (authenticated && itemKeys.length) {
      //   for (const key of itemKeys) {
      //     try {
      //       await this.removeItem(key);
      //     } catch (error) {
      //       // Continue clearing remaining items even if one request fails.
      //     }
      //   }
      // }

      this.items = [];
      this.totalCount = 0;
      this.lastResponse = null;
      this.cartMeta = getDefaultCartMeta();
      window.localStorage.removeItem(CART_ID_STORAGE_KEY);
      window.localStorage.removeItem(CART_STORAGE_KEY);
      window.localStorage.removeItem(CART_META_STORAGE_KEY);

      this.persistCartState();

      // if (authenticated && toNumericCartId(this.cartMeta?.cartId)) {
      //   await this.refreshCartData();
      // }
    },

    async addProductToCart(product) {
      const productId = String(product?.productId || product?.id || "");
      const sku = String(product?.sku || "");

      const cartItem = {
        productId,
        sku,
        quantity: 1,
      };

      if (!isUserAuthenticated()) {
        this.upsertLocalItem(product, 1);
        this.cartMeta = {
          ...this.cartMeta,
          guestSyncPending: true,
        };
        this.persistCartState();
        return null;
      }

      try {
        await this.syncGuestCartAfterLogin();

        const response = await this.addItemWithCartContext([cartItem]);

        this.lastResponse = response;
        this.syncItemsFromResponse(response);
        await this.refreshCartData();

        this.cartMeta = {
          ...this.cartMeta,
          guestSyncPending: false,
        };
        this.persistCartState();

        return response;
      } catch (error) {
        // Do not mix local fallback into authenticated cart display.
        this.cartMeta = {
          ...this.cartMeta,
          guestSyncPending: true,
        };
        this.persistCartState();
        throw error;
      }
    },

    async addComboToCart(products = [], loyaltyCards = []) {
      const normalizedProducts = Array.isArray(products) ? products : [];
      if (!normalizedProducts.length) {
        return null;
      }

      this.cartMeta = {
        ...this.cartMeta,
        loyaltyCards: mergeLoyaltyCards(
          this.cartMeta?.loyaltyCards,
          loyaltyCards,
        ),
      };
      this.persistCartState();

      const cartItems = normalizedProducts.map((product) => ({
        productId: String(product?.productId || product?.id || ""),
        sku: String(product?.sku || ""),
        quantity: 1,
      }));

      if (!isUserAuthenticated()) {
        normalizedProducts.forEach((product) =>
          this.upsertLocalItem(product, 1),
        );
        this.cartMeta = {
          ...this.cartMeta,
          guestSyncPending: true,
        };
        this.persistCartState();
        return null;
      }

      try {
        await this.syncGuestCartAfterLogin();

        const response = await this.addItemWithCartContext(cartItems);

        this.lastResponse = response;
        this.syncItemsFromResponse(response);
        await this.refreshCartData();

        this.cartMeta = {
          ...this.cartMeta,
          guestSyncPending: false,
        };
        this.persistCartState();

        return response;
      } catch (error) {
        this.cartMeta = {
          ...this.cartMeta,
          guestSyncPending: true,
        };
        this.persistCartState();
        throw error;
      }
    },

    async syncGuestCartAfterLogin() {
      if (!isUserAuthenticated()) {
        return null;
      }

      const hadCartIdBeforeSync = Boolean(
        toNumericCartId(this.cartMeta?.cartId),
      );

      const localSnapshot = Array.isArray(this.items)
        ? this.items
            .map((item) => ({
              key: item.key,
              productId: String(item.productId || ""),
              sku: String(item.sku || ""),
              quantity: Number(item.quantity) || 1,
              name: item.name,
              price: item.price,
              image: item.image,
              storeId: item.storeId,
              storeCode: item.storeCode,
            }))
            .filter((item) => item.productId || item.sku)
        : [];

      if (!localSnapshot.length) {
        await this.refreshCartData();
        this.cartMeta = {
          ...this.cartMeta,
          guestSyncPending: false,
        };
        this.persistCartState();
        return null;
      }

      try {
        // Load server cart first when cartId is known; otherwise treat server as empty
        // so local guest items are pushed to create/restore a valid cart.
        if (hadCartIdBeforeSync) {
          await this.refreshCartData();
        }

        const serverQtyByKey = new Map();
        if (hadCartIdBeforeSync) {
          for (const item of this.items) {
            const key = String(item?.key || item?.productId || item?.sku || "");
            if (!key) continue;
            serverQtyByKey.set(key, Number(item?.quantity) || 0);
          }
        }

        let lastAddResponse = null;
        for (const item of localSnapshot) {
          const key = String(item?.key || item?.productId || item?.sku || "");
          const localQty = Number(item.quantity) || 0;
          const serverQty = Number(serverQtyByKey.get(key) || 0);
          const quantityDelta = localQty - serverQty;
          if (quantityDelta <= 0) {
            continue;
          }

          const response = await this.addItemWithCartContext([
            {
              productId: item.productId || undefined,
              sku: item.sku || undefined,
              quantity: quantityDelta,
            },
          ]);

          lastAddResponse = response;
          this.lastResponse = response;
          this.syncMetaFromResponse(response);
        }

        await this.refreshCartData();

        this.cartMeta = {
          ...this.cartMeta,
          guestSyncPending: false,
        };
        this.persistCartState();

        return lastAddResponse;
      } catch (error) {
        // Keep guest cart items visible for retry if merge fails.
        this.items = localSnapshot.map((item) => ({
          key: item.key,
          productId: item.productId,
          sku: item.sku,
          quantity: Number(item.quantity) || 1,
          name: item.name,
          price: item.price,
          image: item.image,
          storeId: item.storeId,
          storeCode: item.storeCode,
        }));
        this.cartMeta = {
          ...this.cartMeta,
          guestSyncPending: true,
        };
        this.persistCartState();
        return null;
      }
    },
  },
});
